import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_JOIN_MODES,
  CAMPAIGN_TRIGGER_ORDER,
  CAMPAIGN_TRIGGERS,
  CampaignModalSection,
  CampaignModalShell,
  CampaignModalSummary,
  CampaignOptionCard,
  CampaignSubChoice,
  hasCampaignJoinRule,
} from '#/entities/campaign'
import type { CampaignJoinMode } from '#/entities/campaign'
import {
  AUDIENCE_BACKTEST_DAYS,
  CampaignScopeSection,
  CampaignTriggerFields,
  toAudienceQuery,
  useCampaignAudienceCount,
  useCampaignEnrolment,
} from '#/features/campaign-enrol'
import type { UseCampaignEnrolmentResult } from '#/features/campaign-enrol'
import type {
  CampaignAudienceQuery,
  CampaignUpdate,
} from '#/shared/api/generated/communication/models'
import { Skeleton } from '#/shared/ui/skeleton'

import {
  toAudienceUpdate,
  useCampaignAudienceEdit,
} from '../model/use-campaign-audience-edit'
import type { CampaignAudienceSeed } from '../model/use-campaign-audience-edit'
import { useCampaignJoinFilters } from '../model/use-campaign-join-filters'
import type { UseCampaignJoinFiltersResult } from '../model/use-campaign-join-filters'
import { CampaignFilterBuilder } from './campaign-filter-builder'

/** The three chips that are real events. "No order 60d" is a filter, and a
 *  trigger campaign cannot become one after the fact. */
const EVENT_TRIGGERS = CAMPAIGN_TRIGGER_ORDER.filter(
  (trigger) => trigger !== CAMPAIGN_TRIGGERS.noOrder60d,
)

/** What the edited rule asks the audience count — the same replay the
 *  new-campaign dialog runs, so the two boxes agree on the same rule. */
function toEditAudience(
  join: CampaignJoinMode,
  enrolment: UseCampaignEnrolmentResult,
  definition: Record<string, unknown>,
): CampaignAudienceQuery {
  return {
    ...(join === CAMPAIGN_JOIN_MODES.filter ? toAudienceQuery(definition) : {}),
    ...(enrolment.selectedProviderIds.length > 0
      ? { provider_ids: enrolment.selectedProviderIds }
      : {}),
    within_days: AUDIENCE_BACKTEST_DAYS,
  }
}

/**
 * The saved mode, shown as the one selected card so the page reads like the
 * new-campaign dialog — with only that card's fields live, since the mode
 * itself is fixed once the campaign exists.
 */
function JoinSection({
  join,
  enrolment,
  filters,
}: {
  join: CampaignJoinMode
  enrolment: UseCampaignEnrolmentResult
  filters: UseCampaignJoinFiltersResult
}) {
  const { t } = useTranslation()
  return (
    <CampaignModalSection label={t('campaigns.modal.edit.joinLabel')}>
      <CampaignOptionCard
        title={t(`campaigns.modal.edit.${join}.title`)}
        description={t(`campaigns.modal.edit.${join}.description`)}
        selected
        onSelect={() => undefined}
      >
        {join === CAMPAIGN_JOIN_MODES.filter && (
          <CampaignSubChoice label={t('campaigns.modal.join.filter.chipLabel')}>
            <CampaignFilterBuilder filters={filters} />
          </CampaignSubChoice>
        )}
        {join === CAMPAIGN_JOIN_MODES.trigger && (
          <CampaignTriggerFields state={enrolment} triggers={EVENT_TRIGGERS} />
        )}
      </CampaignOptionCard>
    </CampaignModalSection>
  )
}

/**
 * The form proper, mounted once the saved answers are known so its state
 * opens on them. The shell around it owns the Save button; the form hands
 * the shell its current answers through `submitRef`, refreshed every render.
 */
function AudienceForm({
  seed,
  open,
  submitRef,
  onCanSaveChange,
}: {
  seed: CampaignAudienceSeed
  open: boolean
  submitRef: React.MutableRefObject<(() => CampaignUpdate) | null>
  /** Save lives on the shell, so whether the answers are savable is reported
   *  up as state — a ref would not re-render the button. */
  onCanSaveChange: (canSave: boolean) => void
}) {
  const { t } = useTranslation()
  const enrolment = useCampaignEnrolment(seed.enrolment)
  const filters = useCampaignJoinFilters(
    seed.join === CAMPAIGN_JOIN_MODES.filter,
    seed.filterDefinition,
  )
  const audience = useCampaignAudienceCount({
    enabled: open && seed.join !== CAMPAIGN_JOIN_MODES.manual,
    query: toEditAudience(seed.join, enrolment, filters.definition),
  })
  useEffect(() => {
    submitRef.current = () =>
      toAudienceUpdate(seed.join, enrolment, filters.definition)
  })
  // Clearing every chip off a rolling filter would PATCH an empty
  // `filter_definition`, which the service refuses the same way it refuses one
  // on create.
  const canSave = hasCampaignJoinRule(seed.join, filters.definition)
  useEffect(() => {
    onCanSaveChange(canSave)
  }, [canSave, onCanSaveChange])
  return (
    <>
      <CampaignScopeSection state={enrolment} showRunsForLabel />
      <JoinSection join={seed.join} enrolment={enrolment} filters={filters} />
      {seed.join !== CAMPAIGN_JOIN_MODES.manual && (
        <CampaignModalSummary
          label={audience.basis ?? t('campaigns.modal.new.previewLabel')}
          headline={audience.headline}
          notes={audience.notes}
        />
      )}
    </>
  )
}

function FormSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-busy="true">
      <Skeleton className="h-3 w-32" />
      <Skeleton className="h-16 w-full rounded-lg" />
      <Skeleton className="h-24 w-full rounded-lg" />
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-28 w-full rounded-lg" />
    </div>
  )
}

interface CampaignAudienceDialogProps {
  campaignId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * The strategy card's Edit. `PATCH /campaigns/{id}` takes the scope, the
 * agents, the trigger or filter and the sources, so those are what it offers;
 * how people join was decided at creation and is shown, not switched.
 */
export function CampaignAudienceDialog({
  campaignId,
  open,
  onOpenChange,
}: CampaignAudienceDialogProps) {
  const { t } = useTranslation()
  const edit = useCampaignAudienceEdit(campaignId, open, () =>
    onOpenChange(false),
  )
  const submitRef = useRef<(() => CampaignUpdate) | null>(null)
  const [canSave, setCanSave] = useState(true)
  return (
    <CampaignModalShell
      open={open}
      onOpenChange={onOpenChange}
      title={t('campaigns.modal.edit.title')}
      description={t('campaigns.modal.edit.description')}
      submitLabel={t('campaigns.modal.edit.submit')}
      submitDisabled={edit.seed == null || edit.isSaving || !canSave}
      onSubmit={() => {
        const body = submitRef.current?.()
        if (body != null) edit.onSave(body)
      }}
    >
      {edit.seed == null ? (
        <FormSkeleton />
      ) : (
        <AudienceForm
          seed={edit.seed}
          open={open}
          submitRef={submitRef}
          onCanSaveChange={setCanSave}
        />
      )}
    </CampaignModalShell>
  )
}
